import { useEffect, useMemo } from 'react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { getFlagURL } from '@/helpers/utils';
import { useUploadFile } from '@/query/queries/common/useUploadFile';
import {
  Button,
  CountrySelect,
  FileUploader,
  InlineAlert,
  InputField,
} from '@scramble/ui';
import { useCheckCountryForSepa } from '@/query/queries/cash/useCheckCountryForSepa';
import { useGetCountries } from '@/query/queries/common/useGetCountries';
import { useCheckIbanNumber } from '@/query/queries/cash/useCheckIbanNumber';
import {
  BANK_ACCOUNT_REVIEW_STATUSES,
  MIN_IBAN_LENGTH,
  VALIDATION_SCHEMA,
} from './schema';
import s from './index.module.scss';

export const Form = ({ data, onSubmit }) => {
  const { t } = useTranslation();
  const { data: countries } = useGetCountries({ is_restricted: false });

  const normalizeIban = iban => (iban || '').replace(/\s+/g, '').toUpperCase();

  const {
    values,
    errors,
    touched,
    dirty,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    setFieldTouched,
    isSubmitting,
    setFieldError,
  } = useFormik({
    onSubmit,
    validationSchema: VALIDATION_SCHEMA(t),
    initialValues: {
      id: data?.id,
      country: data
        ? {
            value: data.country_code,
            code: data.country_code,
            label: data.country,
            flag: getFlagURL(data.country_code),
          }
        : null,
      in_sepa_zone: data ? data.is_sepa_zone : null,
      is_valid_sepa: !!data?.account_number,
      files: data?.bank_statement
        ? [{
            id: data.bank_statement.id,
            name: data.bank_statement.name,
            url: data.bank_statement.url,
            size: data.bank_statement.size,
            mimeType: data.bank_statement.mime_type || data.bank_statement.mimeType,
            status: 'uploaded',
            progress: 100,
            _key: String(data.bank_statement.id),
            original: data.bank_statement,
          }]
        : [],
      beneficiary_name: data?.beneficiary_name || '',
      account_number: data?.account_number || '',
      bank_statement: data?.bank_statement?.id,
      swift: data?.swift || '',
      beneficiary_address: data?.beneficiary_address || '',
      beneficiary_bank: data?.beneficiary_bank || '',
      beneficiary_bank_address: data?.beneficiary_bank_address || '',
    },
  });

  const { data: countrySepaResult, isFetching: isFetchingSepa } =
    useCheckCountryForSepa(values.country?.code);

  useEffect(() => {
    setFieldValue(
      'in_sepa_zone',
      countrySepaResult ? countrySepaResult.in_sepa_zone : false,
    );
  }, [countrySepaResult, setFieldValue]);

  const { data: ibanCheckResult, isFetching: isFetchingIban } = useCheckIbanNumber(
    {
      country_code: values.country?.code,
      iban: values.account_number,
    },
    {
      enabled: !!(
        values.country?.code && values.account_number.length >= MIN_IBAN_LENGTH
      ),
    },
  );

  useEffect(() => {
    setFieldValue(
      'is_valid_sepa',
      ibanCheckResult ? ibanCheckResult.is_valid : false,
    );
  }, [ibanCheckResult, setFieldValue]);

  const { mutateAsync: uploadFileMutation } = useUploadFile();

  const adapter = useMemo(() => ({
    uploadFile: async (file, onProgress) => {
      const response = await uploadFileMutation({ file, onProgress });

      if (response?.status !== 201 || !response.data?.id) {
        throw new Error(
          response?.data?.message || t('bank_accounts:form.not_upload_file'),
        );
      }

      return {
        id: response.data.id,
        name: response.data.name || file.name,
        url: response.data.url,
        mimeType: file.type,
        original: response.data,
      };
    },
  }), [uploadFileMutation, t]);

  return (
    <form onSubmit={handleSubmit} className={s.form}>
      <div className={s.content}>
        <h1 className="f-headline-xs f-500">
          {data
            ? t('bank_accounts:form.edit_bank_account')
            : t('bank_accounts:form.add_bank_account')}
        </h1>

        {data?.review_status === BANK_ACCOUNT_REVIEW_STATUSES.rejected && (
          <InlineAlert
            type="warning"
            title={t('bank_accounts:form.bank_account_rejected')}
            message={data.rejected_reason}
          />
        )}

        <div className={s.sections}>
          <div className={s.section}>
            <div className={s.section_left}>
              <div className="f-body-md f-500">
                {t('bank_accounts:form.bank_account_details')}
              </div>
              <div className="f-body-sm">
                {t('bank_accounts:form.enter_details')}
              </div>
            </div>
            <div className={s.section_right}>
              <CountrySelect
                label={t('bank_accounts:form.bank_country', { required: '*' })}
                placeholder={t('bank_accounts:form.bank_country_placeholder')}
                selected={values.country}
                onChange={country => {
                  setFieldValue('country', country);
                  setTimeout(() => setFieldTouched('country', true), 0);
                }}
                error={touched.country && errors.country}
                options={countries}
              />

              <InputField
                label={t('bank_accounts:form.beneficiary_name', {
                  required: '*',
                })}
                name="beneficiary_name"
                value={values.beneficiary_name}
                onChange={handleChange}
                onBlur={handleBlur}
                onClear={() => setFieldValue('beneficiary_name', '')}
                placeholder={t(
                  'bank_accounts:form.beneficiary_name_placeholder',
                )}
                error={
                  errors.beneficiary_name &&
                  touched.beneficiary_name &&
                  errors.beneficiary_name
                }
              />

              {!isFetchingSepa && !values.in_sepa_zone && (
                <>
                  <InputField
                    label={t('bank_accounts:form.swift_bic_code', {
                      required: '*',
                    })}
                    name="swift"
                    value={values.swift}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onClear={() => setFieldValue('swift', '')}
                    error={errors.swift && touched.swift && errors.swift}
                    placeholder={t(
                      'bank_accounts:form.swift_bic_code_placeholder',
                    )}
                  />

                  <InputField
                    label={t('bank_accounts:form.address', { required: '*' })}
                    name="beneficiary_address"
                    value={values.beneficiary_address}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onClear={() => setFieldValue('beneficiary_address', '')}
                    error={
                      errors.beneficiary_address &&
                      touched.beneficiary_address &&
                      errors.beneficiary_address
                    }
                    placeholder={t('bank_accounts:form.address_placeholder')}
                  />

                  <InputField
                    label={t('bank_accounts:form.bank_name', { required: '*' })}
                    name="beneficiary_bank"
                    value={values.beneficiary_bank}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onClear={() => setFieldValue('beneficiary_bank', '')}
                    error={
                      errors.beneficiary_bank &&
                      touched.beneficiary_bank &&
                      errors.beneficiary_bank
                    }
                    placeholder={t('bank_accounts:form.bank_name_placeholder')}
                  />

                  <InputField
                    label={t('bank_accounts:form.bank_address', {
                      required: '*',
                    })}
                    name="beneficiary_bank_address"
                    value={values.beneficiary_bank_address}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onClear={() =>
                      setFieldValue('beneficiary_bank_address', '')
                    }
                    error={
                      errors.beneficiary_bank_address &&
                      touched.beneficiary_bank_address &&
                      errors.beneficiary_bank_address
                    }
                    placeholder={t(
                      'bank_accounts:form.bank_address_placeholder',
                    )}
                  />
                </>
              )}

              <InputField
                label={t('bank_accounts:form.bank_account_number_iban', {
                  required: '*',
                })}
                name="account_number"
                value={values.account_number}
                onChange={e =>
                  setFieldValue('account_number', normalizeIban(e.target.value))
                }
                onBlur={handleBlur}
                onClear={() => setFieldValue('account_number', '')}
                error={
                  touched.account_number &&
                  (errors.account_number || errors.is_valid_sepa)
                }
                placeholder={t(
                  'bank_accounts:form.bank_account_number_iban_placeholder',
                )}
              />

              {!isFetchingSepa &&
                values.account_number &&
                !values.in_sepa_zone && (
                  <InlineAlert
                    type="warning"
                    message={t('bank_accounts:form.non_sepa_zone_warning')}
                  />
                )}
            </div>
          </div>

          <div className={s.divider} />

          <div className={s.section}>
            <div className={s.section_left}>
              <div className="f-body-md f-500">
                {t('bank_accounts:form.bank_account_proof')}
              </div>
              <div className="f-body-sm">
                {t('bank_accounts:form.bank_account_proof_desc')}
              </div>
            </div>
            <div className={s.section_right}>
              <FileUploader
                label={t('bank_accounts:form.bank_statement', {
                  required: '*',
                })}
                value={values.files}
                onChange={files => {
                  setFieldValue('files', files);
                  setFieldTouched('files', true);
                }}
                onValidationError={errors =>
                  setFieldError('files', errors[0]?.message)
                }
                adapter={adapter}
                accept={['.pdf']}
                maxSize={1 * 1024 * 1024}
                maxFiles={1}
                disableDropzone={!!values.files.length}
                error={touched.files && errors.files}
                labels={{
                  dropText: t('common:file_uploader.drop_text'),
                  browseText: t('common:file_uploader.browse_text'),
                  listAriaLabel: t('common:file_uploader.list_aria_label'),
                  statusUploaded: t('common:file_uploader.status_uploaded'),
                  statusUploading: t('common:file_uploader.status_uploading'),
                  statusFailed: t('common:file_uploader.status_failed'),
                  retryAriaLabel: name =>
                    t('common:file_uploader.retry_aria_label', { name }),
                  removeAriaLabel: name =>
                    t('common:file_uploader.remove_aria_label', { name }),
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className={s.footer}>
        <div className={s.footer_container}>
          <Button
            type="submit"
            theme="brand"
            appearance="filled"
            size="lg"
            label={t('bank_accounts:form.submit_for_review')}
            disabled={
              !dirty ||
              !!Object.keys(errors).length ||
              isSubmitting ||
              !values.files[0]?.id ||
              isFetchingSepa ||
              isFetchingIban
            }
          />
        </div>
      </div>
    </form>
  );
};
